/**
 * Gets the palette color data for the specified tag - red/green/blue,
 * either from the given UID or from the tag itself.
 * Returns an array if the data is immediately available, or a promise
 * which resolves to the data if the data needs to be loaded.
 * Returns undefined if the palette isn't specified.
 *
 * @param {*} item containing the palette colour data and description
 * @param {*} tag is the tag for the palette data
 * @param {*} descriptorTag is the tag for the descriptor
 * @returns Array view containing the palette data, or a promise to return one.
 * Returns undefined if the palette data is absent.
 */
export default function fetchPaletteColorLookupTableData(item, tag, descriptorTag) {
  const { PaletteColorLookupTableUID } = item;
  const paletteData = item[tag];
  if (paletteData === undefined && PaletteColorLookupTableUID === undefined) {
    return;
  }
  // performance optimization - read UID and cache by UID
  return _getPaletteColor(item[tag], item[descriptorTag]);
}


function _getPaletteColor(paletteColorLookupTableData, lutDescriptor) {
  if (!lutDescriptor) {
    return undefined;
  }

  // DICOM PS3.3 C.7.6.3.1.5: a zero value represents 65536 LUT entries.
  const numLutEntries = lutDescriptor[0] || 65536;
  const bits = lutDescriptor[2];

  if (!paletteColorLookupTableData) {
    return undefined;
  }

  const binaryToPaletteColorLUT = binary => {
    // Local P10 parsing through dcmjs yields ArrayBuffer/typed-array values,
    // while DICOMweb metadata yields byte-oriented InlineBinary/BulkDataURI.
    // Normalize all of them before passing the LUT to Cornerstone.
    if (Array.isArray(binary) && binary.length === 1 && ArrayBuffer.isView(binary[0])) {
      binary = binary[0];
    } else if (Array.isArray(binary) && binary.length === 1 && binary[0] instanceof ArrayBuffer) {
      binary = binary[0];
    }

    if (ArrayBuffer.isView(binary) && !(binary instanceof Uint8Array)) {
      if (bits === 16 && binary.BYTES_PER_ELEMENT === 2) {
        return Array.from(binary.slice(0, numLutEntries));
      }

      binary = new Uint8Array(binary.buffer, binary.byteOffset, binary.byteLength);
    }

    const bytes =
      binary instanceof ArrayBuffer
        ? new Uint8Array(binary)
        : binary instanceof Uint8Array
          ? binary
          : Array.isArray(binary)
            ? Uint8Array.from(binary)
            : undefined;

    if (!bytes) {
      return undefined;
    }

    const isEightBitLutStoredInWords = bits === 8 && bytes.length >= numLutEntries * 2;
    const bytesPerEntry = bits === 16 || isEightBitLutStoredInWords ? 2 : 1;
    const lut = new Array(Math.min(numLutEntries, Math.floor(bytes.length / bytesPerEntry)));

    if (bits === 16) {
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      for (let i = 0; i < lut.length; i++) {
        lut[i] = view.getUint16(i * 2, true);
      }
    } else if (isEightBitLutStoredInWords) {
      // Some older DICOM producers allocate 16 bits for every 8-bit LUT
      // entry. DICOM PS3.3 says to detect this from the doubled Value Length;
      // the actual value occupies the low byte and the high byte is padding.
      for (let i = 0; i < lut.length; i++) {
        lut[i] = bytes[i * 2];
      }
    } else {
      for (let i = 0; i < lut.length; i++) {
        lut[i] = bytes[i];
      }
    }

    return lut;
  };

  if (paletteColorLookupTableData.palette) {
    return paletteColorLookupTableData.palette;
  }

  if (
    paletteColorLookupTableData instanceof ArrayBuffer ||
    ArrayBuffer.isView(paletteColorLookupTableData) ||
    Array.isArray(paletteColorLookupTableData)
  ) {
    return binaryToPaletteColorLUT(paletteColorLookupTableData);
  }

  if (paletteColorLookupTableData.InlineBinary) {
    try {
      const bytes = Uint8Array.from(atob(paletteColorLookupTableData.InlineBinary), c =>
        c.charCodeAt(0)
      );
      return (paletteColorLookupTableData.palette = binaryToPaletteColorLUT(bytes));
    } catch (e) {
      console.log("Couldn't decode", paletteColorLookupTableData.InlineBinary, e);
      return undefined;
    }
  }

  if (paletteColorLookupTableData.retrieveBulkData) {
    return paletteColorLookupTableData
      .retrieveBulkData()
      .then(val => (paletteColorLookupTableData.palette = binaryToPaletteColorLUT(val)));
  }

  console.error(`No data found for ${paletteColorLookupTableData} palette`);
}
